import { Filter } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { CuriosityNamespaceType, useCuriosityTranslation } from '@procureai/curiosity-ui-mui';
import { NumberFormatPreset, StackedBarChartProps, formatNumber } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { last6MonthsDateRange } from '~/util/cube';

import { transformDataToStackedBarChartData } from './OrderVolumeCommodityGroupWidget.util';

export interface UseOrderVolumeCommodityResult {
  title: string;
  subTitle: string;
  data: StackedBarChartProps['data'];
  dataConfig: StackedBarChartProps['dataConfig'];
  xAxis: StackedBarChartProps['xAxis'];
  yAxis: StackedBarChartProps['yAxis'];
  isLoading: boolean;
}

interface useOrderVolumeByCommodityGroupProps {
  config: {
    cube: string;
    filters: Filter[];
  };
}

export const useOrderVolumeByCommodityGroup = ({
  config,
}: useOrderVolumeByCommodityGroupProps): UseOrderVolumeCommodityResult => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'OrderVolumeCommodityGroupWidget' });
  const { ct } = useCuriosityTranslation(CuriosityNamespaceType.COMMODITY_GROUP);

  const query = useCubeQuery({
    measures: [`${config.cube}.orderVolume`],
    filters: config.filters,
    timeDimensions: [
      {
        dimension: `${config.cube}.date`,
        granularity: 'month',
        dateRange: last6MonthsDateRange,
      },
    ],
    dimensions: [`${config.cube}.commodityGroupId`],
  });

  const chartData = useMemo(() => {
    const segments =
      query.resultSet?.series()?.map((segment) => ({
        commodityGroup: segment.key.split(',')[0],
        series: segment.series,
      })) || [];

    return transformDataToStackedBarChartData(segments, 'commodityGroup', (labelKey) => ct(labelKey)?.value);
  }, [query.resultSet, ct]);

  const xAxis = {
    label: t('xLabel'),
    tickFormatter: (value: string) => value.split('/')[0],
  };

  const yAxis = {
    label: t('yLabel'),
    tickFormatter: formatNumber(NumberFormatPreset.CURRENCY_COMPACT),
    width: 85,
    padding: {
      top: 5,
    },
  };

  const isLoading = query.isLoading;

  return {
    title: t('title'),
    subTitle: t('subTitle'),
    xAxis,
    yAxis,
    isLoading,
    ...chartData,
  };
};
